import { arrayContains } from '@halo/_utils/array'
import * as changeCase from 'change-case'

/**
 * Deep sets a value in an object given a path.
 * If no object is supplied, a new one will be created;
 *
 * Examples:
 *
 * deepSet('b.c', 'bar', { 'a': 'foo' })
 * -> { 'a': 'bar', 'b': { 'c': 'bar' } }
 *
 * deepSet('b.c', 'bar')
 * -> { 'b': { 'c': 'bar' } }
 */
export function deepSet(path: string, value: any, object: any = null) {
    if (!object) {
        object = {}
    }

    const pathParts = path.split('.')
    if (pathParts.length > 1) {
        const hand = (object[pathParts[0]] = object[pathParts[0]] || {})
        if (typeof hand === 'object' && !Array.isArray(hand)) {
            deepSet(pathParts.splice(1).join('.'), value, hand)
        } else {
            throw new Error(`Cannot deep set value (${value}). Existing value at ${path} was not an object.`)
        }
    } else {
        object[pathParts[0]] = value
    }

    return object
}

/**
 * Merges obj2 into obj1 recursively. If a key existing in obj2 and obj1 but cannot be merge (different types), obj2's
 * value will be chosen.
 */
export function deepMerge(obj1: any, obj2: any) {
    const mergedObject = {
        ...obj1,
    }

    for (const key in obj2) {
        if (obj2.hasOwnProperty(key)) {
            let doMerge = false

            if (key in obj1 && isObject(obj1[key]) && isObject(obj2[key])) {
                doMerge = true
            }

            if (doMerge) {
                mergedObject[key] = deepMerge(obj1[key], obj2[key])
            } else {
                mergedObject[key] = obj2[key]
            }
        }
    }

    return mergedObject
}

export function isObject(value: any): value is Record<string, any> {
    return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export function isPromise(o: any): boolean {
    return typeof o === 'object' && typeof o.then === 'function'
}

// Polyfill for Object.fromEntries
export function objectFromEntries<T>(entries: Array<[string | number, T]>) {
    let obj: { [key: string]: T } = {}
    for (let [key, value] of entries) {
        obj[key] = value
    }
    return obj
}
